import { useState } from "react";
import { Copy, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { InfoBanner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { EmptyState } from "@/components/ui/feedback";
import { Field, Input } from "@/components/ui/input";
import { Modal } from "@/components/ui/modal";
import { Table, TableWrapper } from "@/components/ui/table";
import {
  ErrorState,
  LoadingTable,
  NotAvailableState,
  PermissionDeniedState,
} from "@/components/ui/system-state";
import type { SsoDomainsState } from "@/features/admin/sso/use-sso-domains";
import { formatTemplate, type SsoLocaleCatalog } from "@/i18n/use-sso-locale";
import {
  createSsoDomainChallenge,
  verifySsoDomainChallenge,
  withStepUpRetry,
  type ApiSsoDomainChallenge,
  type SsoDomainMutationResult,
} from "@/lib/api";

/**
 * Admin → Single sign-on → Domains (ALL-4870, SPEC-SSO-7 §2.1/§2.2).
 *
 * Calls only the three operations the SSO admin contract marks
 * `x-alloy-status: served`: listSsoDomains (through useSsoDomains),
 * createSsoDomainChallenge and verifySsoDomainChallenge. Every row, record
 * value, expiry and status is whatever the gateway returned — there is no
 * seeded or invented domain, and a DNS token is only ever rendered from the
 * response that carried it. Both writes go through withStepUpRetry, because the
 * gateway gates them on a fresh AAL2 step-up (GatewayRouteScopes, SPEC-SSO-3
 * §1); the page never invents an MFA prompt of its own and never offers a way
 * around the step-up.
 */

/** Reason codes that mean "this caller may not do this" rather than a transport
 * or server fault. */
const DENIED_REASON_CODES = [
  "HTTP_401",
  "HTTP_403",
  "AUTH_INSUFFICIENT_SCOPE",
  "AUTH_INSUFFICIENT_PERMISSION",
  "AUTH_HUMAN_PRINCIPAL_REQUIRED",
];

/** Reason codes that mean the SSO admin API is not answering here at all, so
 * the page must say so instead of rendering an empty list. `ROUTE_NOT_FOUND` is
 * what the gateway returns for a route it has not registered yet (the same code
 * the hot-wallets pages read), and `NETWORK_ERROR` is
 * `fetchSsoDomains`' own transport-failure code. */
const UNAVAILABLE_REASON_CODES = [
  "ROUTE_NOT_FOUND",
  "HTTP_404",
  "HTTP_502",
  "HTTP_503",
  "HTTP_504",
  "NETWORK_ERROR",
  "AUTH_POLICY_UNAVAILABLE",
];

function statusTone(status: string): "success" | "warning" | "danger" | "neutral" {
  switch (status) {
    case "VERIFIED":
      return "success";
    case "PENDING":
      return "warning";
    case "EXPIRED":
      return "danger";
    default:
      return "neutral";
  }
}

function statusLabel(ui: Record<string, string>, status: string): string {
  return ui[`sso.domains.status.${status}`] ?? ui["sso.domains.status.unknown"];
}

/** Localized text for a refusal reason code, falling back to a template that
 * keeps the raw code visible rather than swallowing it. */
function refusalMessage(ui: Record<string, string>, reasonCode: string | undefined): string | null {
  if (!reasonCode) return null;
  const known = ui[`sso.domains.refusal.${reasonCode}`];
  if (known) return known;
  return formatTemplate(ui["sso.domains.refusal.unknown"], { reasonCode });
}

function verifyOutcomeKey(status: string, kind: "Title" | "Description"): string {
  switch (status) {
    case "VERIFIED":
      return `sso.domains.verifyVerified${kind}`;
    case "PENDING":
      return `sso.domains.verifyNotYet${kind}`;
    case "EXPIRED":
      return `sso.domains.verifyExpired${kind}`;
    case "SUPERSEDED":
      return `sso.domains.verifySuperseded${kind}`;
    default:
      return "sso.domains.verifyFailedTitle";
  }
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

interface Denial {
  message: string;
  supportReference?: string;
  capability?: string;
}

export function AdminSsoDomainsCard({
  canManage,
  catalog,
  domainsState,
}: {
  canManage: boolean;
  catalog: SsoLocaleCatalog;
  /** The domain read is owned by the page and handed down, so the provider
   * form's verified-domain picker, the enable dialog and this table all read
   * one response instead of issuing one request each. */
  domainsState: SsoDomainsState;
}) {
  const ui = catalog.ui;
  const { domains, loading, error, refresh } = domainsState;
  /** The one-time token, kept only for challenges this session created — the
   * API never returns it again, so it is never re-fetched or invented. */
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState<string | null>(null);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [verifyResult, setVerifyResult] = useState<Denial | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addDomain, setAddDomain] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const [addBusy, setAddBusy] = useState(false);
  const [addNotice, setAddNotice] = useState<Denial | null>(null);
  const [created, setCreated] = useState<ApiSsoDomainChallenge | null>(null);

  async function copyValue(key: string, value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      window.setTimeout(() => setCopied((current) => (current === key ? null : current)), 2000);
    } catch {
      // Clipboard access can be denied by the browser. The value is already on
      // screen and selectable, so this is a soft failure, not an error state.
    }
  }

  function denialFor(
    result: SsoDomainMutationResult,
    stepUpFailed: boolean,
    stepUpSupportReference?: string,
  ): Denial | null {
    if (result.ok) return null;
    if (stepUpFailed) {
      return { message: ui["sso.domains.stepUpDescription"], supportReference: stepUpSupportReference };
    }
    return {
      message: refusalMessage(ui, result.reasonCode) ?? ui["sso.domains.verifyFailedTitle"],
      supportReference: result.supportReference,
      capability: result.capability,
    };
  }

  async function handleAdd(event?: React.FormEvent) {
    event?.preventDefault();
    const domain = addDomain.trim();
    if (!domain) {
      setAddError(ui["sso.domains.addRequired"]);
      return;
    }
    setAddError(null);
    setAddNotice(null);
    setAddBusy(true);
    const outcome = await withStepUpRetry(() => createSsoDomainChallenge(domain));
    setAddBusy(false);
    const denial = denialFor(outcome.result, outcome.stepUpFailed, outcome.stepUpSupportReference);
    if (denial) {
      setAddError(denial.message);
      setAddNotice(denial);
      return;
    }
    const challenge = outcome.result.domain;
    if (!challenge?.challengeId) {
      // A 2xx without a challenge id is not something this page can render.
      setAddNotice({ message: ui["sso.domains.recordNotShown"], supportReference: outcome.result.supportReference });
      setAddOpen(false);
      refresh();
      return;
    }
    setCreated(challenge);
    if (challenge.recordValue) {
      setRevealed((current) => ({ ...current, [challenge.challengeId]: challenge.recordValue as string }));
    } else {
      setAddNotice({ message: ui["sso.domains.recordNotShown"], supportReference: challenge.supportReference });
    }
    refresh();
  }

  async function handleVerify(challenge: ApiSsoDomainChallenge) {
    setVerifyingId(challenge.challengeId);
    setVerifyResult(null);
    const outcome = await withStepUpRetry(() => verifySsoDomainChallenge(challenge.challengeId));
    setVerifyingId(null);
    const denial = denialFor(outcome.result, outcome.stepUpFailed, outcome.stepUpSupportReference);
    if (denial) {
      setVerifyResult(denial);
      return;
    }
    const next = outcome.result.domain;
    if (!next?.challengeId) {
      setVerifyResult({
        message: ui["sso.domains.verifyFailedTitle"],
        supportReference: outcome.result.supportReference,
      });
      return;
    }
    setVerifyResult({
      message: `${ui[verifyOutcomeKey(next.status, "Title")]} — ${ui[verifyOutcomeKey(next.status, "Description")]}`,
      supportReference: next.supportReference,
    });
    refresh();
  }

  function openAdd() {
    setAddDomain("");
    setAddError(null);
    setAddNotice(null);
    setCreated(null);
    setAddOpen(true);
  }

  // The "coming next" gap note is rendered once by the page shell (sso-admin),
  // not here, so a state that replaces the card cannot duplicate it.
  if (!canManage) {
    return (
      <PermissionDeniedState
        title={ui["sso.domains.deniedTitle"]}
        description={ui["sso.domains.deniedDescription"]}
      />
    );
  }

  // A refusal that came from the gateway (rather than from the local session
  // check) still carries the server's own reasonCode and D636 support
  // reference, so the operator can quote both at support.
  if (error && DENIED_REASON_CODES.includes(error.reasonCode)) {
    return (
      <PermissionDeniedState
        title={ui["sso.domains.deniedTitle"]}
        description={ui["sso.domains.deniedDescription"]}
        details={[{ k: "reasonCode", v: error.reasonCode }]}
        footnote={<SupportDetails reference={error.supportReference} capability={error.capability} />}
      />
    );
  }

  if (error && UNAVAILABLE_REASON_CODES.includes(error.reasonCode)) {
    return (
      <div className="flex flex-col items-center gap-2">
        <NotAvailableState
          title={ui["sso.domains.unavailableTitle"]}
          description={ui["sso.domains.unavailableDescription"]}
          reason={error.reasonCode}
        />
        <SupportDetails reference={error.supportReference} capability={error.capability} className="justify-center" />
      </div>
    );
  }

  return (
    <Card>
      <CardBody>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>{ui["sso.domains.title"]}</CardTitle>
            <p className="mt-2 max-w-[560px] text-xs leading-relaxed text-muted">
              {ui["sso.domains.description"]}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" size="sm" onClick={refresh} disabled={loading}>
              <RefreshCw aria-hidden className="size-3.5" /> {ui["sso.domains.retry"]}
            </Button>
            <Button size="sm" onClick={openAdd}>
              {ui["sso.domains.addButton"]}
            </Button>
          </div>
        </div>

        {verifyResult && (
          <div data-testid="sso-verify-result">
            <InfoBanner variant="info" className="mt-4">
              {verifyResult.message}
              <SupportDetails reference={verifyResult.supportReference} capability={verifyResult.capability} />
            </InfoBanner>
          </div>
        )}

        {loading && (
          <div className="mt-4">
            <span className="sr-only">{ui["sso.domains.loading"]}</span>
            <LoadingTable rows={3} cols={5} />
          </div>
        )}

        {!loading && error && (
          // RFC 9457: the reasonCode is the machine-readable detail; the
          // server's support reference is rendered through the app's copyable
          // reference component (D636) rather than as dead text.
          <div className="mt-4 flex flex-col items-center gap-2">
            <ErrorState
              title={ui["sso.domains.failedToLoad"]}
              details={error.reasonCode}
              retryLabel={ui["sso.domains.retry"]}
              onRetry={refresh}
            />
            <SupportDetails reference={error.supportReference} capability={error.capability} className="justify-center" />
          </div>
        )}

        {!loading && !error && domains.length === 0 && (
          <div className="mt-4">
            <EmptyState
              card
              title={ui["sso.domains.emptyTitle"]}
              description={ui["sso.domains.emptyDescription"]}
              action={
                <Button size="sm" onClick={openAdd}>
                  {ui["sso.domains.addButton"]}
                </Button>
              }
            />
          </div>
        )}

        {!loading && !error && domains.length > 0 && (
          <div className="mt-4" data-testid="sso-domains-table">
            <TableWrapper>
              <Table className="dense">
                <thead>
                  <tr className="border-b border-line bg-bg">
                    {[
                      "sso.domains.table.domain",
                      "sso.domains.table.status",
                      "sso.domains.table.recordName",
                      "sso.domains.table.recordValue",
                      "sso.domains.table.expires",
                      "sso.domains.table.actions",
                    ].map((key) => (
                      <th
                        key={key}
                        className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted"
                      >
                        {ui[key]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {domains.map((domain) => {
                    const value = revealed[domain.challengeId] ?? domain.recordValue ?? null;
                    return (
                      <tr key={domain.challengeId} className="border-b border-line last:border-b-0">
                        <td className="px-4 py-[9px] whitespace-nowrap font-mono text-[11.5px] text-ink">
                          {domain.domain}
                        </td>
                        <td className="px-4 py-[9px] whitespace-nowrap">
                          <Badge variant={statusTone(domain.status)} size="sm">
                            {statusLabel(ui, domain.status)}
                          </Badge>
                          {domain.verifiedAt && (
                            <span className="ml-2 text-[10px] text-muted">{formatTimestamp(domain.verifiedAt)}</span>
                          )}
                        </td>
                        <td className="px-4 py-[9px] font-mono text-[11px] text-ink">{domain.recordName}</td>
                        <td className="px-4 py-[9px] text-[11px] text-ink">
                          {value ? (
                            <div className="flex items-center gap-2">
                              <code data-testid="sso-record-value" className="break-all font-mono text-[11px]">
                                {value}
                              </code>
                              <Button
                                variant="ghost"
                                size="sm"
                                aria-label={`${ui["sso.domains.copyValue"]} ${domain.domain}`}
                                onClick={() => void copyValue(domain.challengeId, value)}
                              >
                                <Copy aria-hidden className="size-3.5" />
                                {copied === domain.challengeId ? ui["sso.domains.copied"] : null}
                              </Button>
                            </div>
                          ) : (
                            <span className="text-[11px] text-muted">{ui["sso.domains.recordNotShown"]}</span>
                          )}
                        </td>
                        <td className="px-4 py-[9px] whitespace-nowrap text-[11.5px] text-ink">
                          {formatTimestamp(domain.expiresAt)}
                        </td>
                        <td className="px-4 py-[9px] whitespace-nowrap">
                          {domain.status === "PENDING" ? (
                            <Button
                              variant="secondary"
                              size="sm"
                              loading={verifyingId === domain.challengeId}
                              onClick={() => void handleVerify(domain)}
                            >
                              {verifyingId === domain.challengeId
                                ? ui["sso.domains.verifyBusy"]
                                : ui["sso.domains.verifyButton"]}
                            </Button>
                          ) : domain.status === "EXPIRED" ? (
                            <span className="text-[11px] text-muted">{ui["sso.domains.expiredHint"]}</span>
                          ) : (
                            // Verified and superseded rows have no action on
                            // this page (release/delete is still `planned` in
                            // the contract), so say so instead of repeating the
                            // status already shown in the status column.
                            <span className="text-[11px] text-muted">{ui["sso.domains.noAction"]}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrapper>
          </div>
        )}
      </CardBody>

      <Modal
        open={addOpen}
        onOpenChange={(open) => {
          if (!addBusy) setAddOpen(open);
        }}
        title={ui["sso.domains.addTitle"]}
        description={ui["sso.domains.addDescription"]}
        size="md"
        footer={
          created ? (
            <Button variant="secondary" onClick={() => setAddOpen(false)}>
              {ui["sso.domains.addCancel"]}
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setAddOpen(false)} disabled={addBusy}>
                {ui["sso.domains.addCancel"]}
              </Button>
              <Button loading={addBusy} onClick={() => void handleAdd()}>
                {addBusy ? ui["sso.domains.addBusy"] : ui["sso.domains.addSubmit"]}
              </Button>
            </>
          )
        }
      >
        {addNotice && (
          <div data-testid="sso-add-notice">
            <InfoBanner variant="danger">
              {addNotice.message}
              <SupportDetails reference={addNotice.supportReference} capability={addNotice.capability} />
            </InfoBanner>
          </div>
        )}

        {created ? (
          <div data-testid="sso-domain-created">
            <p className="text-xs font-semibold text-ink">{ui["sso.domains.addedTitle"]}</p>
            <p className="mt-1 text-xs text-muted">{ui["sso.domains.recordOnceOnly"]}</p>
            <dl className="mt-3 flex flex-col gap-2 rounded-md border border-line bg-bg px-3 py-3">
              <div className="flex flex-col gap-0.5">
                <dt className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-muted">
                  {ui["sso.domains.table.recordName"]}
                </dt>
                <dd className="font-mono text-[11.5px] text-ink">{created.recordName}</dd>
              </div>
              <div className="flex flex-col gap-0.5">
                <dt className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-muted">
                  {ui["sso.domains.table.recordValue"]}
                </dt>
                <dd className="flex flex-wrap items-center gap-2">
                  <code data-testid="sso-created-record-value" className="break-all font-mono text-[11.5px] text-ink">
                    {revealed[created.challengeId] ?? ui["sso.domains.recordNotShown"]}
                  </code>
                  {revealed[created.challengeId] && (
                    <Button
                      variant="secondary"
                      size="sm"
                      aria-label={ui["sso.domains.copyValue"]}
                      onClick={() => void copyValue(created.challengeId, revealed[created.challengeId] as string)}
                    >
                      <Copy aria-hidden className="size-3.5" />
                      {copied === created.challengeId ? ui["sso.domains.copied"] : ui["sso.domains.copyValue"]}
                    </Button>
                  )}
                </dd>
              </div>
              <div className="flex flex-col gap-0.5">
                <dt className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-muted">
                  {ui["sso.domains.table.expires"]}
                </dt>
                <dd className="text-[11.5px] text-ink">{formatTimestamp(created.expiresAt)}</dd>
              </div>
            </dl>
            <p className="mt-3 text-xs text-muted">{ui["sso.domains.dnsHint"]}</p>
          </div>
        ) : (
          <form id="sso-add-domain-form" onSubmit={handleAdd}>
            <Field
              label={ui["sso.domains.addLabel"]}
              htmlFor="sso-add-domain-input"
              hint={addError ? undefined : ui["sso.domains.addHint"]}
              error={addError ?? undefined}
              required
            >
              <Input
                id="sso-add-domain-input"
                name="domain"
                value={addDomain}
                onChange={(event) => setAddDomain(event.target.value)}
                placeholder={ui["sso.domains.addPlaceholder"]}
                autoComplete="off"
                spellCheck={false}
                error={Boolean(addError)}
                disabled={addBusy}
              />
            </Field>
          </form>
        )}
      </Modal>
    </Card>
  );
}
